import type { UserPosition } from '@/hooks/useUserPosition';
import { formatDistance } from '@/lib/geo';
import type { SiteCheck } from '@/lib/location';
import { LocationNudge } from './LocationNudge';

interface Props {
  stationName: string;
  /** 'far' chega congelado; 'unknown' é recalculado ao vivo pelo caller —
   * vira 'near'/'far' assim que o cliente ativa a localização aqui dentro. */
  check: SiteCheck;
  position: UserPosition;
  onConfirm: () => void;
  onPickAnother: () => void;
  onClose: () => void;
}

/**
 * Trava de "eletroposto errado" do ConfigureCharge (2026-10-08): clientes
 * escolhiam outra estação na lista e o RemoteStart ligava um carregador a km
 * de onde o carro estava. Só abre quando o cliente está longe do eletroposto
 * ou quando não dá pra saber (sem localização) — perto, a recarga inicia
 * direto, sem toque extra.
 */
export function SiteGuardModal({
  stationName,
  check,
  position,
  onConfirm,
  onPickAnother,
  onClose
}: Props) {
  const nudging =
    position.coords === null &&
    position.status !== 'checking' &&
    position.status !== 'unavailable';
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-6 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-sm rounded-3xl bg-bow-cream p-6 text-bow-royal shadow-2xl">
        {check.kind === 'far' ? (
          <>
            <div className="text-lg font-semibold leading-snug">
              Você está a {formatDistance(check.distanceKm)} deste eletroposto
            </div>
            <p className="mt-2 text-sm text-bow-royal/75">
              O <strong>{stationName}</strong> fica longe de onde você está agora.
              Iniciar aqui liga o carregador <strong>deste</strong> eletroposto —
              confira se é nele que o seu carro está plugado.
            </p>
            <div className="mt-5 flex flex-col gap-2.5">
              <button
                type="button"
                onClick={onPickAnother}
                className="rounded-full bg-bow-royal px-4 py-3 text-sm font-semibold text-bow-cream"
              >
                Escolher outro eletroposto
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className="rounded-full border border-bow-royal/15 bg-white px-4 py-3 text-sm font-semibold text-bow-royal"
              >
                Estou aqui, iniciar mesmo assim
              </button>
            </div>
          </>
        ) : check.kind === 'near' ? (
          <>
            <div className="flex items-center gap-2 text-lg font-semibold leading-snug">
              <CheckBadge /> Você está no {stationName}
            </div>
            <p className="mt-2 text-sm text-bow-royal/75">
              Localização confirmada. Pode iniciar a recarga.
            </p>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-full border border-bow-royal/15 bg-white px-4 py-3 text-sm font-semibold text-bow-royal"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className="flex-1 rounded-full bg-bow-royal px-4 py-3 text-sm font-semibold text-bow-cream"
              >
                Iniciar recarga
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-lg font-semibold leading-snug">
              Você está no {stationName}?
            </div>
            <p className="mt-2 text-sm text-bow-royal/75">
              Confira antes de iniciar: a recarga liga o carregador
              <strong> deste</strong> eletroposto.
            </p>
            {nudging && (
              <LocationNudge
                className="mt-4"
                status={position.status}
                locating={position.locating}
                onRequest={position.request}
              />
            )}
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-full border border-bow-royal/15 bg-white px-4 py-3 text-sm font-semibold text-bow-royal"
              >
                Voltar
              </button>
              {/* Com o aviso na tela, o destaque é ATIVAR a localização —
                  confirmar no escuro fica como saída secundária. */}
              <button
                type="button"
                onClick={onConfirm}
                className={`flex-1 rounded-full px-4 py-3 text-sm font-semibold ${
                  nudging
                    ? 'border border-bow-royal/15 bg-white text-bow-royal'
                    : 'bg-bow-royal text-bow-cream'
                }`}
              >
                Sim, iniciar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CheckBadge() {
  return (
    <span
      aria-hidden
      className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-bow-green"
    >
      <svg
        viewBox="0 0 24 24"
        width={14}
        height={14}
        fill="none"
        stroke="#fff"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m5 12 5 5 9-10" />
      </svg>
    </span>
  );
}
